import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { stats, homePartnerLogos } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import SEO, { organizationSchema, websiteSchema } from '../components/SEO';
import SEOContentBlock from '../components/SEOContentBlock';

/* ── Animated contour canvas (same as menu) ── */
const ContoursCanvas = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = Math.min(canvas.offsetWidth, window.innerWidth);
      canvas.height = canvas.offsetHeight;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      const t = timeRef.current;

      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      const numLines = 90;
      for (let i = 0; i < numLines; i++) {
        const baseY = (i / numLines) * H;
        ctx.beginPath();
        for (let x = 0; x <= W; x += 2) {
          const nx = x / W;
          const y = baseY
            + Math.sin(nx * 6.2  + i * 0.38 + t * 0.18) * 22
            + Math.sin(nx * 14   + i * 0.22 - t * 0.11) * 10
            + Math.sin(nx * 3.1  - i * 0.15 + t * 0.07) * 32
            + Math.sin(nx * 22   + i * 0.5  + t * 0.25) * 5
            + Math.sin(nx * 1.8  + i * 0.08 - t * 0.04) * 18;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        const brightness = 0.25 + 0.55 * Math.abs(Math.sin(i * 0.19 + t * 0.05));
        ctx.strokeStyle = `rgba(255,255,255,${brightness})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }

      // Fade toward center from both sides — starts fading right at first letter of title
      // Lines fully visible only on outer ~15%, fade complete at ~32% from each edge
      const fadeStart = W * 0.01; // start fading almost immediately from edge
      const fadeEnd   = W * 0.36; // fully black by 36% from each edge

      // Left fade
      const gL = ctx.createLinearGradient(0, 0, fadeEnd, 0);
      gL.addColorStop(0, 'rgba(0,0,0,0)');
      gL.addColorStop(fadeStart / fadeEnd, 'rgba(0,0,0,0)');
      gL.addColorStop(1, 'rgba(0,0,0,1)');
      ctx.fillStyle = gL;
      ctx.fillRect(0, 0, fadeEnd, H);

      // Right fade
      const gR = ctx.createLinearGradient(W, 0, W - fadeEnd, 0);
      gR.addColorStop(0, 'rgba(0,0,0,0)');
      gR.addColorStop(fadeStart / fadeEnd, 'rgba(0,0,0,0)');
      gR.addColorStop(1, 'rgba(0,0,0,1)');
      ctx.fillStyle = gR;
      ctx.fillRect(W - fadeEnd, 0, fadeEnd, H);

      // Pure black center band (36% of width)
      ctx.fillStyle = '#000';
      ctx.fillRect(fadeEnd, 0, W - fadeEnd * 2, H);

      timeRef.current += 0.012;
      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      ro.disconnect();
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
};

/* ── Reveal hook ── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

/* ── Section with reveal ── */
const RevealDiv = ({ className, children, dir = 'up', delay = 0 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transitionDelay = `${delay}ms`;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  const cls = dir === 'left' ? 'reveal-left' : dir === 'right' ? 'reveal-right' : 'reveal';
  return <div ref={ref} className={`${cls} ${className || ''}`}>{children}</div>;
};

const HERO_SLIDES = [
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/mtiicy2y_DJI_20250112203327_0.jpg.webp',
    alt: 'Transglobal Car Expedition base camp — Geographic South Pole',
    expedition: 'Transglobal Car Expedition',
    date: 'January 10, 2025',
    coords: '90°00\'S  000°00\'E',
    location: 'Geographic South Pole',
    filter: 'contrast(1.05) saturate(1.05) brightness(0.95)',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/7rwx458a_Cr%C3%A9ation%20sans%20titre%20%283%29.jpg',
    alt: 'AeonRV expedition camper on mountain highway — dramatic alpine peaks',
    expedition: 'AeonRV Build',
    date: '2025',
    coords: '40°45\'N  111°52\'W',
    location: 'Mountain West, USA',
    filter: 'contrast(1.04) saturate(1.06)',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/l2dypydb_Cr%C3%A9ation%20sans%20titre.jpg',
    alt: '1987 Mercedes-Benz Unimog custom expedition build — Gulkana River, Alaska',
    expedition: 'Custom Project — 1987 Mercedes-Benz Unimog',
    date: 'August 2021',
    coords: '62°44\'N  144°53\'W',
    location: 'Gulkana River, Alaska, USA',
    filter: 'contrast(1.04) saturate(1.05)',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/5koa01ma_Firefly.jpg',
    alt: 'Arctic Trucks AT44 with TOTAL COMPOSITES camper — Magnetic North Pole expedition',
    expedition: 'Pole to Pole EV Expedition',
    date: 'April 2023',
    coords: '86°29\'N  162°52\'W',
    location: 'Magnetic North Pole',
    filter: 'contrast(1.05) saturate(1.08)',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/5ibvj0zc_DDG04.jpg',
    alt: 'Custom Ford F550 expedition build — Vancouver Island, BC',
    expedition: 'Custom Build — Ford F550',
    date: 'September 2023',
    coords: '49°17\'N  124°50\'W',
    location: 'Vancouver Island, BC, Canada',
    filter: 'contrast(1.04) saturate(1.06)',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/8qnktbpt_Cr%C3%A9ation%20sans%20titre%20%282%29.jpg',
    alt: 'Custom build on Land Rover Defender 130 — rocky mountain terrain',
    expedition: 'Custom Build — Land Rover Defender 130',
    date: '2017',
    coords: '36°52\'N  30°42\'E',
    location: 'Taurus Mountains',
    filter: 'contrast(1.04) saturate(1.05)',
  },
];

/* ── Scroll hint — right side on mobile/tablet, bottom-center on desktop ── */
const ScrollHint = () => {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);

    const t = setTimeout(() => setVisible(true), 5000);
    const onScroll = () => { if (window.scrollY > 50) setVisible(false); };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', check);
    };
  }, []);

  const scrollDown = () => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });

  if (isMobile) {
    // Mobile/tablet: bottom-right, above geo info, no conflict with arrows or progress
    return (
      <button
        onClick={scrollDown}
        className="absolute z-20 flex flex-col items-center gap-1.5 cursor-pointer border-none bg-transparent"
        style={{
          right: 20,
          bottom: 52, // above geo info (bottom-8 = 32px + some padding)
          opacity: visible ? 0.6 : 0,
          transition: 'opacity 1.2s ease',
          pointerEvents: visible ? 'all' : 'none',
        }}
      >
        <p className="text-white font-bold uppercase"
          style={{ fontSize: '0.5rem', letterSpacing: '0.2em', writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
          SCROLL
        </p>
        <div style={{
          width: 1,
          height: 28,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0))',
          animation: visible ? 'scrollDrop 1.6s ease-in-out infinite' : 'none',
        }} />
      </button>
    );
  }

  // Desktop: bottom center
  return (
    <button
      onClick={scrollDown}
      className="absolute left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 cursor-pointer border-none bg-transparent"
      style={{
        bottom: 16,
        opacity: visible ? 0.55 : 0,
        transition: 'opacity 1.2s ease',
        pointerEvents: visible ? 'all' : 'none',
      }}
    >
      <p className="text-white font-bold uppercase" style={{ fontSize: '0.55rem', letterSpacing: '0.22em' }}>Scroll</p>
      <div style={{
        width: 1,
        height: 32,
        background: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0))',
        animation: visible ? 'scrollDrop 1.6s ease-in-out infinite' : 'none',
      }} />
    </button>
  );
};

const HeroSlider = ({ startDelay = 0 }) => {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const [autoPlayStarted, setAutoPlayStarted] = useState(false);
  const timerRef = useRef(null);

  // Slide 0 = fade only (10s view), slides 1-4 = pan (trigger at 75% of 16s = 12s)
  const getInterval = (idx) => idx === 0 ? 10000 : 12000;
  const FADE_DURATION = 1600; // slower smooth fade

  const triggerNextRef = useRef(null);

  const goTo = (idx) => {
    if (idx === active || fading) return;
    setFading(true);
    clearInterval(timerRef.current);
    setTimeout(() => {
      setActive(idx);
      setFading(false);
      timerRef.current = setInterval(() => triggerNextRef.current(), getInterval(idx));
    }, FADE_DURATION);
  };

  const triggerNext = useCallback(() => {
    setFading(true);
    clearInterval(timerRef.current);
    setTimeout(() => {
      setActive(a => {
        const next = (a + 1) % HERO_SLIDES.length;
        timerRef.current = setInterval(() => triggerNextRef.current(), getInterval(next));
        return next;
      });
      setFading(false);
    }, FADE_DURATION);
  }, []);

  triggerNextRef.current = triggerNext;

  const prev = () => goTo((active - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const next = () => goTo((active + 1) % HERO_SLIDES.length);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setAutoPlayStarted(true);
      timerRef.current = setInterval(() => triggerNextRef.current(), getInterval(0));
    }, startDelay);
    
    return () => {
      clearTimeout(startTimer);
      clearInterval(timerRef.current);
    };
  }, [startDelay]);

  const slide = HERO_SLIDES[active];

  return (
    <section className="relative h-screen overflow-hidden">
      {HERO_SLIDES.map((s, i) => (
        <img
          key={i === active ? `${s.src}-${active}` : s.src}
          src={s.src} alt={s.alt}
          className={`absolute inset-0 w-full h-full object-cover${i > 1 && i === active ? ' hero-pan' : ''}`}
          style={{ filter: s.filter, opacity: i === active ? (fading ? 0 : 1) : 0, transition: `opacity ${1600}ms cubic-bezier(0.4,0,0.2,1)`, zIndex: i === active ? 1 : 0 }}
        />
      ))}
      <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.7) 100%)' }} />

      {/* Left arrow */}
      <button onClick={prev} className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center border transition-all duration-200 hover:bg-white hover:border-white"
        style={{ borderColor: 'rgba(255,255,255,0.35)' }}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M6.5 2L3.5 5L6.5 8" stroke="white" strokeWidth="1.3" strokeLinecap="round"/></svg>
      </button>

      {/* Right arrow */}
      <button onClick={next} className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center border transition-all duration-200 hover:bg-white hover:border-white"
        style={{ borderColor: 'rgba(255,255,255,0.35)' }}>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3.5 2L6.5 5L3.5 8" stroke="white" strokeWidth="1.3" strokeLinecap="round"/></svg>
      </button>

      <ScrollHint />

      {/* Progress line */}
      <div className="absolute bottom-0 left-0 right-0 z-20" style={{ height: 2, backgroundColor: 'rgba(255,255,255,0.1)' }}>
        <div key={`${active}-${fading}`}
          style={{ height: '100%', backgroundColor: '#E8520A', width: '0%',
            animation: fading ? 'none' : `progressSlide ${active === 0 ? 10000 : 12000}ms linear forwards` }}
        />
      </div>

      {/* Geographic info */}
      <div className="absolute bottom-8 left-8 z-20" style={{ opacity: fading ? 0 : 1, transition: 'opacity 0.8s ease' }}>
        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ fontFamily: 'Inter, sans-serif', color: '#E8520A' }}>{slide.expedition}</p>
        <p className="text-xs font-medium tracking-widest opacity-50 mb-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>{slide.date}</p>
        <p className="text-xs font-medium tracking-widest opacity-40" style={{ fontFamily: 'Inter, sans-serif' }}>{slide.coords} — {slide.location}</p>
      </div>

      {/* Vertical DISCOVER — desktop only (ScrollHint handles mobile/tablet) */}
      <div className="hidden lg:flex absolute right-8 bottom-8 z-20 flex-col items-center">
        <p className="vertical-text text-xs font-bold tracking-widest opacity-60 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>DISCOVER</p>
        <div className="scroll-line" />
      </div>
    </section>
  );
};


/* ── Animated underline span ── */
const AnimUnderline = ({ children }) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      setActive(entry.isIntersecting);
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <span ref={ref} className={`anim-underline ${active ? 'wave' : ''}`}>
      {children}
    </span>
  );
};

/* ── CountUp number animation ── */
const CountUp = ({ value }) => {
  const ref = useRef(null);
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);

  // Parse numeric part and suffix (e.g. "500+" → 500, "+")
  const num = parseInt(value.replace(/\D/g, ''), 10);
  const suffix = value.replace(/[0-9]/g, '');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        obs.unobserve(el);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const duration = 3000;
    const steps = 60;
    const interval = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      // Ease out cubic
      const ease = 1 - Math.pow(1 - progress, 3);
      setDisplayed(Math.round(num * ease));
      if (step >= steps) { setDisplayed(num); clearInterval(timer); }
    }, interval);
    return () => clearInterval(timer);
  }, [started, num]);

  return <span ref={ref}>{displayed}{suffix}</span>;
};

const Home = () => {
  const { t, tx, language } = useLanguage();
  return (
  <div style={{ backgroundColor: '#000', color: '#fff', overflowX: 'hidden', maxWidth: '100vw' }}>
    <SEO
      title="Composite Truck Campers & Expedition Shells"
      description="Total Composites builds premium 4-season composite truck camper shells — slide-in and flatbed — for overland adventurers worldwide. Zero condensation. Zero thermal transfer. Ships globally."
      path="/"
      keywords="composite truck camper, expedition truck camper shell, 4 season truck camper, fiberglass truck camper, overland camper, flatbed camper shell, slide-in camper shell, Total Composites"
      jsonLd={[organizationSchema, websiteSchema]}
    />
    <HeroSlider startDelay={3000} />

    {/* ── GLOBAL LEADER DARK STATEMENT ── */}
      <section className="py-32 px-6 text-center relative overflow-hidden" style={{ backgroundColor: '#000' }}>
        <ContoursCanvas />
        <div className="relative z-10">
          <p className="text-xs font-bold uppercase tracking-widest mb-8 opacity-40">Since 2015 — Victoria, BC, Canada</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black uppercase leading-tight mb-10 max-w-5xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}
          >
            {language === 'fr' ? (
              <>Le campeur port&eacute; <AnimUnderline>le plus avanc&eacute;</AnimUnderline><br />au monde.</>
            ) : (
              <>The World's <AnimUnderline>Most Advanced</AnimUnderline><br />Truck Camper Shell.</>
            )}
          </h2>
          <div className="max-w-3xl mx-auto mb-14">
            <p className="text-center font-semibold mb-5 overflow-x-auto"
              style={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.6rem, 1.5vw, 0.8rem)', letterSpacing: '0.1em', fontFamily: 'Inter, sans-serif' }}>
              {language === 'fr' ? 'Panneaux de conception allemande · Zéro pont thermique · 100 % sans bois' : 'German-engineered panels · Zero thermal bridging · 100% wood-free'}
            </p>
            <p className="text-center leading-loose mb-4"
              style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', letterSpacing: '0.08em', fontFamily: 'Inter, sans-serif' }}>
              {language === 'fr' ? 'Quand les équipes d\'expédition veulent le meilleur, elles choisissent TOTAL COMPOSITES.' : 'When expedition teams need the best, they choose TOTAL COMPOSITES.'}
            </p>
            <p className="text-center leading-loose"
              style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', letterSpacing: '0.06em', fontFamily: 'Inter, sans-serif' }}>
              {language === 'fr' ? 'Un niveau de qualité éprouvé aux deux pôles — approuvé par des constructeurs et entreprises en Amérique du Nord, en Europe et dans le monde entier.' : 'A level of quality proven at both poles — trusted by builders and companies across North America, Europe, and worldwide.'}
            </p>
          </div>
          <Link
            to="/why-total-composites"
            className="inline-block border border-white text-white text-xs font-bold uppercase tracking-widest px-10 py-4 hover:bg-white hover:text-black transition-all duration-300"
          >
            {language === 'fr' ? 'Découvrir la différence' : 'Discover the Difference'}
          </Link>
        </div>
      </section>

      {/* ── STATS TICKER ── */}
      <div className="border-t border-b py-12" style={{ borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#000' }}>
        <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {stats.map((stat, i) => (
            <RevealDiv key={stat.label?.en || `stat-${i}`} delay={i * 80}>
              <div className="text-4xl lg:text-5xl font-black mb-1" style={{ color: '#E8520A', fontFamily: 'Inter, sans-serif' }}>
                <CountUp value={stat.value} />
              </div>
              <div className="text-xs uppercase tracking-widest opacity-50">{tx(stat.label)}</div>
            </RevealDiv>
          ))}
        </div>
      </div>

      {/* ── PRODUCTS / MODELS ── */}
      <section className="py-32 px-6" style={{ backgroundColor: '#000' }}>
        <RevealDiv className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-widest mb-4 opacity-40">{language === 'fr' ? 'Écosystème de produits' : 'Product Ecosystem'}</p>
          <h2 className="text-3xl lg:text-5xl font-black uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {language === 'fr' ? 'Nos systèmes' : 'Our Systems'}
          </h2>
        </RevealDiv>

        {/* Two featured cards */}
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6 mb-10">
          {[
            {
              badge: t('common.new') + ' 2026',
              title: t('nav.truckCampers'),
              desc: language === 'fr' ? 'Le campeur porté composite le plus avancé. 100 % sans bois. Fabriqué en usine. Prêt à monter.' : 'The most advanced composite camper shell. 100% wood-free. Factory-built. Ready to mount.',
              img: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/1m4rt0jp_tc_wolverine82.jpg',
              href: '/products?tab=pre-assembled',
            },
            {
              badge: t('common.new') + ' 2026',
              title: t('nav.keiTruck'),
              desc: language === 'fr' ? 'Caisson cargo composite ultra-léger pour camions Kei. Isolation R-14. Intérieur de qualité alimentaire.' : 'Ultra-light insulated composite cargo box for Kei trucks. R-14 insulation. Food-safe interior.',
              img: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/sqjrg936_ChatGPT%20Image%20Feb%209%2C%202026%2C%2003_19_04%20PM.png',
              href: '/products?tab=kei-truck',
            },
          ].map((p, i) => (
            <RevealDiv key={p.href} dir={i === 0 ? 'left' : 'right'} delay={i * 120}>
              <Link to={p.href} className="group block relative overflow-hidden aspect-square">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.1) 60%)' }} />
                <div className="absolute inset-0 p-8 flex flex-col justify-between">
                  <span className="self-end text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ backgroundColor: '#E8520A', color: '#fff' }}>{p.badge}</span>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-black text-white uppercase mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>{p.title}</h3>
                    <p className="text-gray-300 text-sm leading-relaxed mb-5">{p.desc}</p>
                    <span className="text-xs font-bold uppercase tracking-widest border-b border-white pb-0.5 text-white group-hover:text-orange-400 group-hover:border-orange-400 transition-colors">
                      {language === 'fr' ? 'Explorer →' : 'Explore →'}
                    </span>
                  </div>
                </div>
              </Link>
            </RevealDiv>
          ))}
        </div>

        {/* Other products 3-col */}
        <div className="max-w-6xl mx-auto grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { title: t('nav.flatKits'), desc: t('nav.flatKitsDesc'), href: '/products?tab=expedition-kits' },
            { title: t('nav.truckBodies'), desc: t('nav.truckBodiesDesc'), href: '/products?tab=truck-bodies' },
            { title: t('nav.customProjects'), desc: t('nav.customProjectsDesc'), href: '/products?tab=custom' },
          ].map((p, i) => (
            <RevealDiv key={p.href} delay={i * 80}>
              <Link
                to={p.href}
                className="group block border border-white border-opacity-10 p-6 hover:border-opacity-40 transition-all duration-300"
              >
                <h4 className="text-base font-black text-white uppercase mb-1 group-hover:text-orange-400 transition-colors">{p.title}</h4>
                <p className="text-gray-500 text-xs leading-relaxed mb-4">{p.desc}</p>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500 group-hover:text-orange-400 transition-colors">{language === 'fr' ? 'Voir →' : 'View →'}</span>
              </Link>
            </RevealDiv>
          ))}
        </div>

        {/* CTA — centered under Truck Bodies row */}
        <div className="text-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-white border border-white px-10 py-4 hover:bg-white hover:text-black transition-all duration-300"
          >
            {language === 'fr' ? 'Découvrir tous nos systèmes' : 'Discover All Our Systems'} <ArrowRight size={13} />
          </Link>
        </div>

      </section>

      {/* ── TESTIMONIAL ── */}
      <section className="py-12 px-6 text-center" style={{ backgroundColor: '#000' }}>
        <RevealDiv>
          <a
            href="https://www.truckcampermagazine.com/news/introducing-total-composites-truck-campers/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-bold uppercase tracking-widest mb-8 opacity-40 hover:opacity-70 transition-opacity inline-block"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Truck Camper Magazine ↗
          </a>
          <a
            href="https://www.truckcampermagazine.com/news/introducing-total-composites-truck-campers/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <blockquote
              className="text-2xl sm:text-3xl lg:text-4xl text-white leading-relaxed max-w-4xl mx-auto mb-8 hover:opacity-80 transition-opacity cursor-pointer"
              style={{ fontFamily: 'Oswald, sans-serif', fontStyle: 'italic', fontWeight: 400 }}
            >
              {language === 'fr'
                ? '« Un campeur porté TOTAL COMPOSITES ne transfère ni chaleur ni froid de l\'extérieur. Rien ne peut geler. Aucune condensation sur les murs. »'
                : '"A TOTAL COMPOSITES camper doesn\'t transfer heat or cold from the outside. Nothing can freeze. No condensation on the walls."'}
            </blockquote>
          </a>
          <p className="text-xs font-bold uppercase tracking-widest opacity-40">{language === 'fr' ? 'C\'est pour cette raison que nous le faisons!' : "That's Why We Do It."}</p>
        </RevealDiv>
      </section>

      {/* ── EXPEDITIONS ── */}
      <section className="py-32 px-6" style={{ backgroundColor: '#000' }}>
        <RevealDiv className="text-center mb-16">
          <p className="text-xs font-bold uppercase tracking-widest mb-4 opacity-40">{language === 'fr' ? 'Éprouvé sur le terrain' : 'Field Proven'}</p>
          <h2 className="text-3xl lg:text-5xl font-black uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {language === 'fr' ? 'Expéditions réelles' : 'Real Expeditions'}
          </h2>
        </RevealDiv>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-6">
          {[
            {
              label: 'April 2023',
              title: 'Magnetic North Pole',
              desc: '24 days. -39°C. Arctic Trucks AT44 + TOTAL COMPOSITES Bobcat 8\' habitat. The first truck camper shell ever used on a magnetic North Pole vehicle expedition.',
              img: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/e70o39w2_image.png',
              href: '/expedition?tab=north-pole',
            },
            {
              label: '2024–2025',
              title: 'Transglobal Car Expedition',
              desc: '475 days. 92,000+ km. 42 countries. First pole-to-pole circumnavigation by vehicle. TOTAL COMPOSITES habitats are now permanently based at the South Pole for future expeditions.',
              img: 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/b4ri2mlk_DJI_20250112203327_0.jpg.webp',
              href: '/expedition?tab=transglobal',
            },
          ].map((exp, i) => (
            <RevealDiv key={exp.href} dir={i === 0 ? 'left' : 'right'} delay={i * 120}>
              <Link to={exp.href} className="group block relative overflow-hidden" style={{ height: 420 }}>
                <img src={exp.img} alt={exp.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.1) 55%)' }} />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A' }}>{exp.label}</p>
                  <h3 className="text-2xl font-black text-white uppercase mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>{exp.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed mb-5">{exp.desc}</p>
                  <span className="text-xs font-bold uppercase tracking-widest border-b border-white pb-0.5 text-white group-hover:text-orange-400 group-hover:border-orange-400 transition-colors">
                    {language === 'fr' ? 'Lire l\'histoire →' : 'Read the Story →'}
                  </span>
                </div>
              </Link>
            </RevealDiv>
          ))}
        </div>
      </section>

      {/* ── FOR PRIVATE & PRO ── */}
      <section className="py-12 px-6 text-center" style={{ backgroundColor: '#000' }}>
        <RevealDiv>
          <h2 className="text-3xl lg:text-5xl font-black text-white leading-tight mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span style={{ color: '#E8520A' }}>{language === 'fr' ? 'Un système.' : 'One System.'}</span> {language === 'fr' ? 'Chaque application.' : 'Every Application.'}
          </h2>
          <p className="max-w-2xl mx-auto text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {language === 'fr' ? 'Que vous soyez un particulier équipant votre camion pour l\'aventure ou un fabricant professionnel construisant pour vos clients — TOTAL COMPOSITES offre le même système composite haut de gamme. Directement de l\'usine, conçu pour durer des décennies.' : 'Whether you\'re an individual outfitting your truck for adventure or a professional fabricator building for clients — TOTAL COMPOSITES delivers the same premium composite system. Direct from the factory, engineered for decades of use, available to builders and buyers alike.'}
          </p>
        </RevealDiv>
      </section>

      {/* ── CTA / SCHEDULE ── */}
      <section className="relative overflow-hidden" style={{ minHeight: 520 }}>
        <img
          src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/b16ffjyd_image.png"
          alt="Arctic Trucks AT44 on snow"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.3) 100%)' }} />
        <div className="relative z-10 flex flex-col items-center justify-center text-center py-36 px-6" style={{ minHeight: 520 }}>
          <RevealDiv>
            <p className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: '#E8520A', letterSpacing: '0.2em' }}>
              {language === 'fr' ? 'Aucune route requise' : 'No Roads Required'}
            </p>
            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase mb-6 leading-none"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {language === 'fr' ? <>Allez plus loin.<br />Restez plus longtemps.<br />Ne craignez rien.</> : <>Go Further.<br />Stay Longer.<br />Fear Nothing.</>}
            </h2>
            <p className="max-w-lg mx-auto mb-12 text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {language === 'fr' ? 'Le campeur porté TOTAL COMPOSITES 2026 est conçu pour les extrêmes — hivers arctiques, chaleur désertique et tout ce qui se trouve entre les deux. Construit une fois. Construit correctement. Construit pour durer.' : 'The 2026 TOTAL COMPOSITES shell is engineered for the extremes — Arctic winters, desert heat, and everything in between. Built once. Built right. Built to outlast every trail you\'ll ever run.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/products?tab=pre-assembled"
                className="px-10 py-4 text-xs font-bold uppercase tracking-widest text-white transition-all duration-300 hover:opacity-85"
                style={{ backgroundColor: '#E8520A' }}
              >
                {language === 'fr' ? 'Explorer les modèles 2026' : 'Explore 2026 Models'}
              </Link>
              <Link
                to="/contact"
                className="px-10 py-4 text-xs font-bold uppercase tracking-widest text-white border border-white hover:bg-white hover:text-black transition-all duration-300"
              >
                {language === 'fr' ? 'Demander un devis' : 'Get a Quote'}
              </Link>
            </div>
          </RevealDiv>
        </div>
      </section>

      {/* SEO Content Block */}
      <SEOContentBlock title="About Our Technology">
        <p>Total Composites is a Canadian-engineered manufacturer of premium composite truck camper shells and expedition truck body panels, headquartered in Victoria, British Columbia. Our composite panels combine fiberglass-reinforced polymer (FRP) skins with high-density foam cores, bonded with aerospace-grade adhesives to create structures that are simultaneously lightweight, waterproof, condensation-free, and thermally inert. Unlike traditional aluminum or wood-framed camper construction, our panels transfer no heat and no cold — making every Total Composites shell a true four-season camper, from below -36°F in the Canadian Arctic to summer heat across North American deserts.</p>
        <p>Our truck camper shell lineup includes four models designed to fit the broadest range of popular trucks: the Lynx (mid-size slide-in for Toyota Tacoma and Jeep Gladiator), the Wolf (full-size slide-in for F-150, Ram 1500, and Silverado), the Bobcat (flatbed camper for F-250, F-350, and one-ton trucks), and the Wolverine (flatbed with rear departure angle for extreme overland use). Each shell ships as a flat-pack kit to our authorized builder/partners across North America, who assemble and custom-finish your camper to specification.</p>
        <p>Composite truck camper shells from Total Composites are an industry-standard choice for overlanding, off-grid travel, vanlife-adjacent builds, and long-range expedition vehicles. Our patented FRP pultrusion connection system eliminates thermal bridging entirely — the structural problem responsible for mold, rust, and condensation in virtually every traditional RV and truck camper on the market. With over 500 units delivered and zero reported structural failures or delaminations, Total Composites is recognized as the global leader in composite expedition truck bodies.</p>
        <p>Whether you're searching for a composite truck camper, an overland camper shell, or a condensation-free 4-season expedition vehicle — Total Composites builds the lightweight, fiberglass flatbed and slide-in camper shells trusted by overlanders across North America and worldwide.</p>
      </SEOContentBlock>

    </div>
  );
};

export default Home;
